import { useState } from "react";
import { useSettingsContext } from "./useSettingsContext";
import { useAuthContext } from "./useAuthContext";
import { useAccountContext } from "./useAccountContext";
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export const useReports = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(undefined);
    const { dispatch, isWinAuth } = useSettingsContext();
    const [isLoading, setIsLoading] = useState(false);
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { account } = useAccountContext();
    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            // console.log('onLeave Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    const getReportParams = async (sId) => {
        setIsLoading(true)
        let resp = {};
        await axiosInstance.get(`${clientApi}api/reports/GetReportParams`, {
            params: {
                username: username,
                sId: sId
            }
        })
            .then((response) => {
                // * This gets from OASISPARAM which is always single line
                setIsLoading(false)
                // console.log(response)
                resp = response.data;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
        return resp;
    };

    const getReportParamsFiltered = async (sSQL, cntrls) => {
        let resp = {};
        const params = {
            username: username,
            dSQL: sSQL,
            dCntrl: cntrls
        };

        await axiosInstance.post(`${clientApi}api/reports/GetReportParamsFiltered`, params)
            .then((response) => {
                resp = response.data;
            })
            .catch((error) => {
                console.log(error);
                throw error;
            });
        return resp;
    };

    const getSSRSReportLink = async (cntrls, selectedRepCode) => {
        let resp = {};
        setIsLoading(true);

        const params = {
            username: username,
            sID: selectedRepCode,
            dCntrl: cntrls
        };

        await axiosInstance.post(`${clientApi}api/reports/GenReportLink`, params)
            .then((response) => {
                setIsLoading(false);
                resp = response.data;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false);
                throw error;
            });
        return resp;
    };

    const getMonitors = async () => {
        let resp = {};
        setIsLoading(true);
        await axiosInstance.get(`${clientApi}api/reports/GetMonitors`, {
            params: {
                username: username
            }
        })
            .then((response) => {
                resp = response.data;
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                throw error;
            });
        setIsLoading(false)
        return resp;
    };

    const getSSRSEmbeded = async (repPath, format) => {
        let resp = {};
        setIsLoading(true);

        const params = {
            username: username,
            reportPath: repPath,
            repName: 'Report',
            format: format,
        };
        await axiosInstance.get(`${clientApi}api/SsrReports/GetReport`, {
            params,
            responseType: 'blob',
        })
            .then((res) => {
                const url = URL.createObjectURL(res.data);
                resp = url;
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
                throw error;
            });
        setIsLoading(false);
        return resp;
    };


    return { getReportParams, getReportParamsFiltered, getSSRSReportLink, getSSRSEmbeded, getMonitors, error, isLoading }
}